import { useNotifications } from '@sparklayer-io/dashboard/store/notifications';
import { applyApiStatusPolicy } from '@sparklayer-io/spark-dash-core/lib/api-status-policy';
import { ApiResponseError } from '@sparklayer-io/spark-dash-core/types/errors';
import type { ApiHandlerContract } from '@sparklayer-io/spark-dash-core/types/handlers/api';
import axios, { type AxiosResponse, isAxiosError } from 'axios';

const { notify } = useNotifications.getState();

/**
 * The site the proxy endpoints are addressed to. Set from the matched `$siteId/$siteEnv` route
 * before any page renders (see `setCurrentSite`).
 */
export type HostSite = { siteId: string; siteEnv: string };

let currentSite: HostSite | null = null;

export function setCurrentSite(site: HostSite | null): void {
  currentSite = site;
}

const client = axios.create({
  baseURL: '/dashboard-api',
  // The browser-default xhr adapter can't suppress redirects; the fetch adapter can, which the
  // Sanctum CSRF-cookie request relies on via `maxRedirects: 0`.
  adapter: 'fetch',
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

// Reauthenticate by reloading, unless the `/user` probe itself came back unauthenticated.
function handleStatusError(status: number | undefined, url?: string): void {
  if (status === undefined) return;
  applyApiStatusPolicy(status, {
    notify: (title, message, type) => notify(title, message, type),
    onAuthExpired: () => {
      if (!url || url.indexOf('/user') === -1) window.location.reload();
    },
  });
}

// `proxy()` can request an `arraybuffer`/`text` responseType for a successful download, which
// leaves an error body undecoded - normalize it back to JSON the way a default `json` request's
// error body already is.
function parseErrorData(data: unknown): unknown {
  const text = data instanceof ArrayBuffer ? new TextDecoder().decode(data) : data;
  if (typeof text !== 'string') return text;
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

function toData<T>(res: AxiosResponse): T {
  if (res.status === 204) return undefined as T;
  return res.data as T;
}

function rethrow(err: unknown, url?: string): never {
  if (!isAxiosError(err) || !err.response) {
    // A refused/network failure never reached the server - let it propagate unwrapped, so
    // downstream status checks (`isApiErrorStatus`) treat only real HTTP responses as matches.
    throw err;
  }
  handleStatusError(err.response.status, err.config?.url || url);
  throw new ApiResponseError(err.response.status, parseErrorData(err.response.data));
}

export const apiHandler: ApiHandlerContract = {
  async fetch(req) {
    try {
      const res = await client.request({
        method: req.method,
        url: req.path,
        data: req.payload,
        maxRedirects: req.maxRedirects,
      });
      return toData(res);
    } catch (err) {
      rethrow(err, req.path);
    }
  },

  async proxy(req) {
    if (!currentSite) {
      throw new Error('siteId and siteEnv are required to call the sparkApi endpoint');
    }
    const { siteId, siteEnv } = currentSite;
    try {
      const res = await client.request({
        method: 'POST',
        url: `/${siteId}/${siteEnv}/proxy-api`,
        responseType: req.responseType,
        data: {
          method: req.method,
          url: req.path,
          body: req.payload,
          content_type: req.contentType,
          params: req.params,
          headers: req.headers,
        },
      });
      return toData(res);
    } catch (err) {
      rethrow(err, req.path);
    }
  },
};
